/**
 * Step 0 — Automatic mode.
 *
 * Two body states:
 *   - no file picked → dropzone (drag-and-drop + click-to-browse)
 *   - file picked   → FileSelectedCard with metadata + "Change file"
 * Start Analysis CTA sits below either state. In-flight while
 * `status === 'generating'` (button shows spinner, dropzone disabled).
 */
import React, { useRef, useState } from 'react';
import type { UploadFlowState } from '../stepBodyProps';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import styles from './AutomaticMode.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// DataTableAny/ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fileTypeLabel(file: File): string {
  const ext = file.name.split('.').pop()?.toUpperCase() ?? 'FILE';
  return ext.length > 6 ? 'FILE' : ext;
}

export default function AutomaticMode({ status, error, onStartAnalysis }: UploadFlowState) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const isGenerating = status === 'generating';

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (isGenerating) return;
    e.preventDefault();
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (isGenerating) return;
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };
  const onClickZone = () => {
    if (isGenerating) return;
    inputRef.current?.click();
  };
  const onClear = () => {
    if (isGenerating) return;
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionBody}>
        <div
          role="button"
          tabIndex={isGenerating ? -1 : 0}
          aria-disabled={isGenerating}
          className={`${styles.dropzone} ${isGenerating ? styles.dropzoneDisabled : ''}`}
          onClick={onClickZone}
          onKeyDown={(e) => !isGenerating && (e.key === 'Enter' || e.key === ' ') && onClickZone()}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <div className={styles.dropzoneIcon}><Icon name="upload" size={24} /></div>
          <div className={styles.dropzoneTitle}>Drop a document or click to browse</div>
          <div className={styles.dropzoneHint}>PDF, DOCX, PPTX, or PNG/JPG — anything the analysis agent can parse.</div>
        </div>

        {file && (
          <div className={styles.fileCard}>
            <div className={styles.fileCardIcon}><Icon name="document" size={20} /></div>
            <div className={styles.fileCardInfo}>
              <div className={styles.fileCardName} title={file.name}>{file.name}</div>
              <div className={styles.fileCardMeta}>
                <span className={styles.fileCardType}>{fileTypeLabel(file)}</span>
                <span className={styles.fileCardSep}>·</span>
                <span>{formatBytes(file.size)}</span>
              </div>
            </div>
            <ButtonAny
              variant="ghost"
              size="icon-sm"
              onClick={onClear}
              disabled={isGenerating}
              aria-label="Remove file"
            >
              <Icon name="close" size={16} />
            </ButtonAny>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx,.pptx,.txt,.md,.png,.jpg,.jpeg"
          onChange={onChange}
          disabled={isGenerating}
          style={{ display: 'none' }}
        />

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.cardActions}>
          <ButtonAny
            variant="default"
            loading={isGenerating}
            disabled={!file}
            onClick={() => file && onStartAnalysis(file)}
          >
            {isGenerating ? 'Starting analysis…' : 'Start Analysis'}
          </ButtonAny>
        </div>
      </div>
    </section>
  );
}
